import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import axiosInstance from "../../utils/axiosConfig";

function LandingOptionsAdmin() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const response = await axiosInstance.get("/landing_options");
      setOptions(response.data);
    } catch (error) {
      console.error("Error fetching landing options:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (option = null) => {
    setEditingOption(option);
    setOpenDialog(true);
    setSelectedImage(null);
  };

  const handleCloseDialog = () => {
    setEditingOption(null);
    setOpenDialog(false);
    setSelectedImage(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Estás seguro de eliminar este registro?")) {
      try {
        await axiosInstance.delete(`/landing_options/${id}`);
        fetchOptions();
      } catch (error) {
        console.error("Error deleting option:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    // Using nested parameters here
    formData.append("landing_option[key]", e.target.key.value);
    formData.append("landing_option[value]", e.target.value.value);
    formData.append("landing_option[active]", e.target.active.value === "true");
    formData.append("landing_option[option_type]", e.target.option_type.value);
    if (selectedImage) {
      formData.append("landing_option[image]", selectedImage);
    }

    try {
      if (editingOption) {
        await axiosInstance.patch(
          `/landing_options/${editingOption.id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        await axiosInstance.post("/landing_options", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      fetchOptions();
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving option:", error);
    }
  };

  // Define key options for the select component.
  const keyOptions = [
    { value: "landing_image", label: "Landing Image" },
    { value: "interest_text", label: "Header Text" },
    { value: "instagram", label: "instagram" },
    { value: "whatsapp", label: "whatsapp" },
    { value: "linkedin", label: "linkedin" },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Administrar Landing Options
      </Typography>
      <Button
        variant="contained"
        onClick={() => handleOpenDialog()}
        sx={{ mb: 2 }}
      >
        Agregar Landing Option
      </Button>

      {loading ? (
        <Typography>Cargando...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Option Type</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options.map((option) => (
                <TableRow key={option.id}>
                  <TableCell>{option.id}</TableCell>
                  <TableCell>{option.key}</TableCell>
                  <TableCell>{option.value}</TableCell>
                  <TableCell>{option.active ? "Sí" : "No"}</TableCell>
                  <TableCell>{option.option_type}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenDialog(option)}
                      sx={{ mr: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(option.id)}
                    >
                      Eliminar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo para agregar/editar registro */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>
          {editingOption ? "Editar Landing Option" : "Agregar Landing Option"}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 2 }}
            id="landing-option-form"
          >
            <FormControl fullWidth margin="dense">
              <InputLabel id="key-label">Key</InputLabel>
              <Select
                labelId="key-label"
                name="key"
                defaultValue={editingOption ? editingOption.key : ""}
                label="Key"
                required
              >
                {keyOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Value"
              name="value"
              defaultValue={editingOption ? editingOption.value : ""}
              fullWidth
              required
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="active-label">Active</InputLabel>
              <Select
                labelId="active-label"
                name="active"
                defaultValue={
                  editingOption ? editingOption.active.toString() : "true"
                }
                label="Active"
              >
                <MenuItem value="true">Sí</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel id="option-type-label">Option Type</InputLabel>
              <Select
                labelId="option-type-label"
                name="option_type"
                defaultValue={
                  editingOption ? editingOption.option_type : "text"
                }
                label="Option Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="social">Social</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" component="label">
                Subir Imagen
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedImage(e.target.files[0]);
                    }
                  }}
                />
              </Button>
              {selectedImage && (
                <Typography variant="caption" sx={{ ml: 2 }}>
                  {selectedImage.name}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mt: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button form="landing-option-form" type="submit" variant="contained">
            {editingOption ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LandingOptionsAdmin;
